import { ReactElement } from "react";
import {
  IContentAreaProps,
  IDataContext, IIndicator,
  IMenuButton,
  ISelectedNode,
  IViewController,
  SchemaNodeType
} from "../../types";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import EventNoteIcon from "@mui/icons-material/EventNote";
import MonitorIcon from "@mui/icons-material/Monitor";

import PowerOnOff from "./meltshop/powerOnOff";
import CrewDetail from "./meltshop/crewDetail";
import DelaysReport from "./meltshop/delays";
import InterruptionDashboard from "../interruptions/interruptionDashboard";
import MsReportPage from "../meltShop/reports/reportPage";
import RmReportPage from "../mill/reports/reportPage";
import ShiftReport from "./mill/shiftReport/rollingMill/shiftReport";
import ShiftReportMS from "./meltshop/shiftReport/shiftReport";
import BBCReport from "./mill/bbc";
import SystemInfoComponent from "./systemInfo/systemInfo";
import ViewCompact from "@mui/icons-material/ViewCompact";
import YieldReport from "./mill/yieldReport";

class Module implements IViewController {

  private powerOnOffButton: IMenuButton = {
    title: "Power On / Off",
    icon: <FormatListBulletedIcon/>,
    color: "blue",
    dataContext: {
      title: "Power On / Off",
      module: "reports",
      subModule: "meltShop/powerOnOff"
    }
  };

  private crewDetailButton: IMenuButton = {
    title: "Crew Detail",
    icon: <FormatListBulletedIcon/>,
    color: "green",
    dataContext: {
      title: "Crew Detail",
      module: "reports",
      subModule: "meltShop/crewDetail"
    }
  };

  private delayReportButton: IMenuButton = {
    title: "Delay Report",
    icon: <FormatListBulletedIcon/>,
    color: "orange",
    dataContext: {
      title: "Delay Report",
      module: "reports",
      subModule: "meltShop/delayReport"
    }
  };

  private reportGroupButton: IMenuButton = {
    title: "Reports",
    icon: <FormatListBulletedIcon/>,
    color: "blueGray",
    children: [this.powerOnOffButton, this.crewDetailButton, this.delayReportButton],
    dataContext: {
      title: "Reports",
      module: "reports",
      subModule: "reportGroup"
    }
  };


  private chartsButton: IMenuButton = {
    title: "Charts",
    icon: <FormatListBulletedIcon/>,
    color: "purple",
    dataContext: {
      title: "Charts",
      module: "reports",
      subModule: "charts"
    }
  };

  private meltshopButton: IMenuButton = {
    title: "Meltshop",
    icon: <FormatListBulletedIcon/>,
    color: "green",
    dataContext: {
      title: "Meltshop",
      module: "reports",
      subModule: "report/ms"
    }
  };

  private rollingMillButton: IMenuButton = {
    title: "Rolling Mill",
    icon: <FormatListBulletedIcon/>,
    color: "cyan",
    dataContext: {
      title: "Rolling Mill",
      module: "reports",
      subModule: "report/rm"
    }
  };

  private shiftReportWireRodButton: IMenuButton = {
    title: "Shift Report",
    icon: <EventNoteIcon/>,
    color: "cyan",
    dataContext: {
      title: "Shift Report",
      module: "reports",
      subModule: "shiftReport/wireRod"
    }
  };

  private shiftReportMeltshopButton: IMenuButton = {
    title: "Shift Report",
    icon: <EventNoteIcon/>,
    color: "cyan",
    dataContext: {
      title: "Shift Report",
      module: "reports",
      subModule: "shiftReport/meltshop"
    }
  };

  private bbcReportButton: IMenuButton = {
    title: "BBC Report",
    icon: <EventNoteIcon/>,
    color: "blue",
    dataContext: {
      title: "BBC Report",
      module: "reports",
      subModule: "bbcReport"
    }
  };

  private millReportGroupButton: IMenuButton = {
    title: "Reports",
    icon: <FormatListBulletedIcon/>,
    color: "blue",
    children: [this.bbcReportButton],
    dataContext: {
      title: "Reports",
      module: "reports",
      subModule: "rmReportGroup"
    }
  };

  private systemInfoButton: IMenuButton = {
    title: "Sys Info",
    icon: <MonitorIcon/>,
    color: "orange",
    dataContext: {
      title: "Sys Info",
      module: "reports",
      subModule: "report/sysInfo"
    }
  };

  private yieldReportButton: IMenuButton = {
    title: "Yield Report",
    icon: <ViewCompact/>,
    color: "orange",
    dataContext: {
      title: "Yield Report",
      module: "reports",
      subModule: "reports/yield-report"
    }
  };


  private buttons = [
    this.reportGroupButton,
    this.crewDetailButton,
    this.delayReportButton,
    this.chartsButton,
    this.meltshopButton,
    this.rollingMillButton,
    this.shiftReportWireRodButton,
    this.shiftReportMeltshopButton,
    this.millReportGroupButton,
    this.systemInfoButton,
    this.yieldReportButton
  ];

  getModuleName(): string{
    return "reports";
  }

  getView (dataContext: IDataContext, props: IContentAreaProps): ReactElement {
    if (dataContext.subModule === "meltShop/powerOnOff") {
      return <PowerOnOff parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "meltShop/crewDetail") {
      return <CrewDetail parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "meltShop/delayReport") {
      return <DelaysReport parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "charts") {
      return <InterruptionDashboard parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "report/ms") {
      return <MsReportPage parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "report/rm") {
      return <RmReportPage parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "shiftReport/wireRod") {
      return <ShiftReport parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "shiftReport/meltshop") {
      return <ShiftReportMS parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "bbcReport") {
      return <BBCReport parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "report/sysInfo") {
      return <SystemInfoComponent parentSize={props.parentSize} />;
    }

    if (dataContext.subModule === "reports/yield-report") {
      return <YieldReport parentSize={props.parentSize} />;
    }

    return <div />;
  }

  getButtons(node: ISelectedNode): IMenuButton[] {
    return node.type === SchemaNodeType.Area ? [this.reportGroupButton] : [];
  }

  getButton(subModule: string): IMenuButton | undefined {
    return this.buttons.find(x => x.dataContext && x.dataContext.subModule === subModule);
  }

  getIndicator(dataContext: IIndicator): ReactElement | undefined {
    if (dataContext.component === "") {
      return <div />;
    }

    return undefined;
  }
}

const instance = new Module();

export default instance;
